import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatPriceCents } from "@/components/services/service-meta";
import { tagColorFor } from "@/components/patients/PatientTagInput";
import { CatalogoBusca, combina } from "@/components/catalogo/CatalogoBusca";
import { BotaoAdicionar } from "@/components/catalogo/BotaoAdicionar";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { AdicionarDaBaseDialog } from "@/components/catalogo/base/AdicionarDaBaseDialog";
import { useToggleFlash } from "@/components/catalogo/useToggleFlash";
import { MaterialLineDialog, type MaterialLineEditTarget } from "./MaterialLineDialog";

const thCls = "px-2 py-1.5 text-left font-semibold";

type Line = MaterialLineEditTarget & {
  active: boolean;
  tags: string[];
  originGlobalId: string | null;
  origemNaBaseId: string | null;
  subcategory: { id: string; label: string } | null;
  supplier: { id: string; name: string | null } | null;
};

const SEM_SUBCATEGORIA = "__sem__";

/**
 * Linha do catálogo. Clicar em qualquer parte dela edita o material — checkbox e lixeira param a
 * propagação porque têm destino próprio. O nome é um `button` para o teclado alcançar por Tab.
 *
 * O checkbox ganha destaque temporário verde/vermelho ao ativar/desativar.
 */
function MaterialRow({
  line,
  disabled,
  onToggle,
  onEdit,
  onDelete,
  onTag,
}: {
  line: Line;
  disabled: boolean;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  onTag: (tag: string) => void;
}) {
  const { flash, handleToggle } = useToggleFlash(onToggle);
  return (
    <tr
      onClick={disabled ? undefined : onEdit}
      className={cn(
        "border-b border-zinc-100 transition-colors duration-500 dark:border-border/50",
        flash === "green" && "bg-emerald-50 dark:bg-emerald-950/30",
        flash === "red" && "bg-red-50 dark:bg-red-950/30",
        !flash && "hover:bg-zinc-50 dark:hover:bg-white/5",
        !disabled && "cursor-pointer",
      )}
    >
      <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={line.active} disabled={disabled} onCheckedChange={handleToggle} aria-label={`Ativar/desativar ${line.name}`} />
      </td>
      <td className="px-2 py-1.5 text-zinc-700 dark:text-foreground/80">{line.materialCategory.label}</td>
      <td className="px-2 py-1.5 text-zinc-700 dark:text-foreground/80">{line.manufacturer.name}</td>
      <td className="px-2 py-1.5 text-zinc-900 dark:text-foreground">
        <button
          type="button"
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          aria-label={`Editar ${line.name}`}
          className="flex items-center gap-1.5 text-left disabled:opacity-50"
        >
          {line.name}
          {line.originGlobalId === null && !line.origemNaBaseId && (
            <span
              title="Criado por você"
              aria-label="Criado por você"
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500 dark:bg-primary"
            />
          )}
          {line.indirectConsumption && (
            <span
              title="Consumo indireto — fora do custo de cada peça"
              className="rounded bg-zinc-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-zinc-600 dark:bg-muted dark:text-muted-foreground"
            >
              Indireto
            </span>
          )}
        </button>
        {(line.tags ?? []).length > 0 && (
          // Clicar na tag busca por ela: uma marca que não leva a lugar nenhum é só enfeite.
          <span className="mt-0.5 flex flex-wrap gap-1" onClick={(e) => e.stopPropagation()}>
            {line.tags.map((t) => {
              const cor = tagColorFor(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => onTag(t)}
                  title={`Filtrar por "${t}"`}
                  style={{ backgroundColor: cor.bg, color: cor.text }}
                  className="rounded px-1.5 py-0.5 text-[9.5px] font-medium"
                >
                  {t}
                </button>
              );
            })}
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right tabular-nums text-zinc-700 dark:text-foreground/80">
        {line.purchasePriceCents != null ? formatPriceCents(line.purchasePriceCents) : "—"}
        {line.unit && <span className="ml-1 text-zinc-400">/{line.unit}</span>}
      </td>
      <td className="px-2 py-1.5 text-zinc-500 dark:text-muted-foreground">{line.supplier?.name ?? "—"}</td>
      <td className="px-2 py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex items-center gap-0.5">
          <button
            type="button"
            disabled={disabled}
            onClick={onDelete}
            className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            aria-label={`Excluir ${line.name}`}
            title="Excluir"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </span>
      </td>
    </tr>
  );
}

export function InsumosSection() {
  const utils = trpc.useUtils();
  const listQuery = trpc.prosthesis.catalog.myMaterialLines.useQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [daBase, setDaBase] = useState(false);
  useParametroDaUrl("adicionar", (v) => v === "insumos" && setDaBase(true));
  const [editTarget, setEditTarget] = useState<MaterialLineEditTarget | null>(null);
  const [busca, setBusca] = useState("");
  /** Categoria que o diálogo deve trazer pronta — vem do container em que se clicou. */
  const [categoriaNova, setCategoriaNova] = useState<string | null>(null);

  function abrirNovo(subcategoryId: string | null) {
    setEditTarget(null);
    setCategoriaNova(subcategoryId);
    setDialogOpen(true);
  }

  const toggle = trpc.prosthesis.catalog.setMaterialLineEnabled.useMutation({
    onSuccess: () => utils.prosthesis.catalog.myMaterialLines.invalidate(),
    onError: (e) => toast.error(e.message || "Erro ao atualizar insumo."),
  });
  const remove = trpc.prosthesis.catalog.deleteCustomMaterialLine.useMutation({
    onSuccess: () => {
      toast.success("Insumo excluído.");
      utils.prosthesis.catalog.myMaterialLines.invalidate();
      utils.prosthesis.catalog.materialLines.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir insumo."),
  });

  const todas = useMemo(() => (listQuery.data ?? []) as unknown as Line[], [listQuery.data]);

  // Busca por tudo que identifica o material na tela: quem procura "Talmax" está atrás do
  // fabricante, quem procura "embalagem" está atrás da subcategoria.
  const linhas = useMemo(
    () =>
      todas.filter(
        (l) =>
          combina(l.name, busca) ||
          combina(l.manufacturer.name, busca) ||
          combina(l.materialCategory.label, busca) ||
          combina(l.subcategory?.label, busca) ||
          (l.tags ?? []).some((t) => combina(t, busca)),
      ),
    [todas, busca],
  );

  // Agrupado por `subcategory` — que na tela se chama CATEGORIA (Brocas, Resinas, Embalagem), é
  // como o laboratório pensa o estoque. O campo `materialCategory`, que a tela chama de
  // subcategoria, é a química. Nenhum dos dois é o que o dentista escolhe: isso é o cardápio
  // (`LabMaterialOption`), no painel acima. Os nomes do código e da interface são trocados de
  // propósito; ver a nota em `MaterialCategory` no schema.
  const grupos = useMemo(() => {
    const mapa = new Map<string, { label: string; itens: Line[] }>();
    for (const l of linhas) {
      const chave = l.subcategory?.id ?? SEM_SUBCATEGORIA;
      if (!mapa.has(chave)) mapa.set(chave, { label: l.subcategory?.label ?? "Sem categoria", itens: [] });
      mapa.get(chave)!.itens.push(l);
    }
    // "Sem categoria" por último: é o balde do que ainda não foi organizado.
    return [...mapa.entries()]
      .sort((a, b) =>
        a[0] === SEM_SUBCATEGORIA ? 1 : b[0] === SEM_SUBCATEGORIA ? -1 : a[1].label.localeCompare(b[1].label, "pt-BR"),
      )
      .map(([id, g]) => ({ id, ...g }));
  }, [linhas]);

  const ocupado = toggle.isPending || remove.isPending;

  return (
    <div id="insumos-cadastro">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">Insumos</p>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            O que você compra, agrupado pela sua categoria. Preço de compra e fator de perda
            alimentam a ficha técnica e o cálculo de estoque.
          </p>
        </div>
        <BotaoAdicionar topo onClick={() => setDaBase(true)}>
          Adicionar insumo
        </BotaoAdicionar>
        <AdicionarDaBaseDialog tipo="insumos" open={daBase} onClose={() => setDaBase(false)} onCriarNovo={() => abrirNovo(null)} />
      </div>

      <CatalogoBusca
        valor={busca}
        onChange={setBusca}
        placeholder="Buscar por insumo, fabricante, categoria, subcategoria ou tag"
        achados={linhas.length}
      />

      <div id="insumos" className="mt-3">
        {linhas.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
            {busca.trim() ? "Nenhum insumo com essa busca." : "Nenhum insumo cadastrado ainda."}
          </p>
        ) : (
          grupos.map((g) => (
            // Cada subcategoria num container próprio: com 55 materiais numa tabela contínua, a
            // separação era só um rótulo cinza e as listas se misturavam ao rolar.
            <div
              key={g.id}
              className="mt-3 rounded-lg border border-zinc-100 p-3 first:mt-0 dark:border-border/60"
            >
              <p className="flex items-baseline gap-1.5 text-xs font-semibold text-zinc-700 dark:text-foreground/80">
                {g.label}
                <span className="text-[10px] font-normal tabular-nums text-zinc-400 dark:text-muted-foreground/70">
                  {g.itens.length} {g.itens.length === 1 ? "insumo" : "insumos"}
                </span>
              </p>
              <table className="mt-1.5 w-full text-xs">
                <thead className="border-b border-zinc-100 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
                  <tr>
                    <th className={thCls}>Ativo</th>
                    <th className={thCls}>Subcategoria</th>
                    <th className={thCls}>Fabricante</th>
                    <th className={thCls}>Linha</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Compra</th>
                    <th className={thCls}>Fornecedor</th>
                    <th className="px-2 py-1.5 text-right font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.itens.map((l) => (
                    <MaterialRow
                      key={l.id}
                      line={l}
                      disabled={ocupado}
                      onToggle={(enabled) => toggle.mutate({ materialLineId: l.id, enabled })}
                      onEdit={() => {
                        setEditTarget(l);
                        setDialogOpen(true);
                      }}
                      onTag={setBusca}
                      onDelete={() => {
                        if (window.confirm(`Excluir o insumo "${l.manufacturer.name} — ${l.name}"?`))
                          remove.mutate({ materialLineId: l.id });
                      }}
                    />
                  ))}
                </tbody>
              </table>
              <div className="mt-1.5">
                <BotaoAdicionar
                  onClick={() => abrirNovo(g.id === SEM_SUBCATEGORIA ? null : g.id)}
                  title={
                    g.id === SEM_SUBCATEGORIA
                      ? "Novo insumo sem categoria"
                      : `Novo insumo em ${g.label}`
                  }
                >
                  {g.id === SEM_SUBCATEGORIA ? "Adicionar sem categoria" : `Adicionar em ${g.label}`}
                </BotaoAdicionar>
              </div>
            </div>
          ))
        )}
      </div>

      <MaterialLineDialog
        open={dialogOpen}
        editTarget={editTarget}
        categoriaInicial={categoriaNova}
        onClose={() => {
          setDialogOpen(false);
          setEditTarget(null);
        }}
      />
    </div>
  );
}
